// Located evidence for the three interstate gas lines near the Dalton, GA research site:
// every point is a place where a public document pins a diameter, capacity, valve, meter or
// interconnect fact to the line, with its source and its straight-line distance to the parcel.
// Positions are milepost-anchored facts placed along OpenStreetMap corridor geometry
// (screening accuracy), except where the source gives the location itself.
// Assembled 2026-10-19 from FERC eLibrary documents (CP15-117 EA and order, CP01-80 order,
// CP21-13 review), Federal Register notices, operator postings and PHMSA annual reports.
import type { FeatureCollection, Point } from 'geojson';

export type GeorgiaEvidenceProperties = {
  id: string;
  line: 'SNG' | 'ETNG' | 'Transco';
  lineName: string;
  label: string;        // short map label, e.g. 20″
  kind: 'diameter' | 'meter' | 'valve' | 'interconnect' | 'terminus' | 'origin';
  value: string;        // the fact, in words
  distanceMi: number;   // straight-line from the parcel edge
  located: string;      // how the position was fixed
  source: string;
  changeNote: string;   // whether the fact changes between here and the site
};

const SNG = 'Southern Natural Gas · Chattanooga Branch / Loop';
const ETNG = 'East Tennessee Natural Gas · Murray Lateral (line 3500-1)';
const TRANSCO = 'Transco · Dalton Lateral (DALT-A-T-T-T)';

const pt = (id: string, lon: number, lat: number, p: Omit<GeorgiaEvidenceProperties, 'id'>) => ({
  type: 'Feature' as const,
  id,
  properties: { id, ...p },
  geometry: { type: 'Point' as const, coordinates: [lon, lat] },
});

export const GEORGIA_EVIDENCE: FeatureCollection<Point, GeorgiaEvidenceProperties> = {
  type: 'FeatureCollection',
  features: [
    // Southern Natural Gas
    pt('sng-us41', -84.944244, 34.696495, {
      line: 'SNG', lineName: SNG, label: '12″?', kind: 'diameter',
      value: 'Nearest approach to the site (US-41 crossing). No public document states the diameter at this point. Bracketed by 12-inch evidence 11.5 mi north (Catoosa) and 15 mi south (Gordon); SNG reports 388 mi of 12-inch and 245 mi of 14-inch in Georgia (PHMSA 2024). Posted segment design: Chat Line-Rome 162,500 Dth/d, Chat Line-Chattanooga 73,500 Dth/d.',
      distanceMi: 2.37, located: 'nearest point of the OSM way to the parcel union',
      source: 'NPMS viewer (operator, system); PHMSA 2024 Part H; SNG point capacity posting 2026-10-19',
      changeNote: 'Diameter unverified here; the 2020 14-to-20-inch replacement (CP21-13) is at the branch origin in Cleburne County, Alabama, not near Dalton.',
    }),
    pt('sng-catoosa-tie', -85.023271, 34.875627, {
      line: 'SNG', lineName: SNG, label: '12″', kind: 'diameter',
      value: '12-inch Chattanooga Branch Line at milepost 101.44, the Cleveland Branch Line tie-in (1994 certificate notice).',
      distanceMi: 11.5, located: 'junction of the OSM Cleveland Branch way with the Chattanooga Branch way',
      source: 'Federal Register 1994-10-26 (CP94-682)',
      changeNote: 'Nearest documented diameter north of the site.',
    }),
    pt('sng-calhoun', -84.974226, 34.481897, {
      line: 'SNG', lineName: SNG, label: '12″', kind: 'diameter',
      value: '12-inch Chattanooga Branch Line and 12-inch Branch Loop Line (with a 4-inch Rome-Calhoun Line) lowered under the South Calhoun Bypass at SR 53 (2016 cultural-resource survey, report 9671).',
      distanceMi: 14.95, located: 'nearest point of the OSM branch to the Calhoun crossover; survey location approximate',
      source: 'University of Georgia Archaeology report 9671 (2016), SNG AFE 207775',
      changeNote: 'Nearest documented diameter south of the site; the loop runs beside the branch here and past Dalton.',
    }),
    // East Tennessee Natural Gas
    pt('etng-nearest', -84.932814, 34.718304, {
      line: 'ETNG', lineName: ETNG, label: '20″', kind: 'diameter',
      value: 'Nearest approach of the Murray Lateral to the site. The lateral is 20-inch for its whole 26.88 miles (2001 order), and all of ETNG\'s 26.64 Georgia miles report as 20-inch (PHMSA 2024).',
      distanceMi: 2.94, located: 'nearest point of the OSM way to the parcel union',
      source: 'FERC order 2001-10-11 (CP01-80); PHMSA 2024 Part H; NPMS viewer',
      changeNote: 'No diameter change anywhere on the lateral.',
    }),
    pt('etng-smith', -84.919542, 34.710508, {
      line: 'ETNG', lineName: ETNG, label: 'end', kind: 'terminus',
      value: 'Murray Lateral terminus at the Oglethorpe Thomas A. Smith plant (MP 26.88): up to 165,000 Dth/d firm of the 170,000 Dth/d the project created (2001 order).',
      distanceMi: 3.64, located: 'end vertex of the OSM way',
      source: 'FERC order 2001-10-11 (CP01-80)',
      changeNote: 'Plant delivery; the lateral was sized for it.',
    }),
    pt('etng-dalton-meter', -84.897995, 34.777401, {
      line: 'ETNG', lineName: ETNG, label: 'meter', kind: 'meter',
      value: 'Dalton / Cartersville meter station with regulators at MP 20.89 (Whitfield County): 5,000 Dth/d for Dalton Utilities and the City of Cartersville (2001). In service 2002-10-31.',
      distanceMi: 6.72, located: 'MP 20.89 measured back 5.99 mi from the terminus along the OSM way (approximate)',
      source: 'FERC order 2001-10-11; FERC notice 2001-03-20; in-service letter 20021031-3021',
      changeNote: 'An existing ETNG delivery into Dalton Utilities.',
    }),
    pt('etng-sng-interconnect', -84.927957, 34.805969, {
      line: 'ETNG', lineName: ETNG, label: 'SNG→ETNG', kind: 'interconnect',
      value: 'ETNG meter MR 59200 "SNG Whitfield" (SNG point 606100, design 27,000 Dth/d): restricted to 0 Dth/d since 2025-10-01 "due to SNG pressures being lower than ETNG\'s typical operating pressure".',
      distanceMi: 7.32, located: 'shared node of the OSM ETNG and SNG loop ways (interconnect position inferred)',
      source: 'Enbridge critical notice 184347 (2026-10-18); SNG point capacity posting',
      changeNote: 'Only public statement about pressure near the site; it concerns the SNG side of this meter.',
    }),
    // Transco
    pt('transco-looper', -84.921611, 34.71065, {
      line: 'Transco', lineName: TRANSCO, label: '20″ end', kind: 'terminus',
      value: 'Looper Bridge Road meter station at the lateral terminus (EA MP 109.3, Murray County): 208,000 Dth/d design (2016 order); posted design 346,728 MMBtu/d with 223,294 scheduled on 2026-10-19. The 20-inch Segment 3 runs 53.8 mi from the Beasley Road meter in Bartow County to here.',
      distanceMi: 3.52, located: 'end vertex of the OSM way',
      source: 'FERC EA 2016-03-31 table A.4.a-1; order 2016-08-03 (CP15-117); 1Line posting 2026-10-19',
      changeNote: 'Diameter is 20-inch from MP 56.5 (Bartow) to the terminus; it steps up to 24-inch and 30-inch only far upstream toward Compressor Station 115.',
    }),
    pt('transco-whitfield-exit', -84.910639, 34.710936, {
      line: 'Transco', lineName: TRANSCO, label: '20″', kind: 'diameter',
      value: 'The lateral crosses Whitfield County from EA MP 107.3 to 108.5 (1.2 mi), 20-inch, with no collocation; this point is MP 108.5.',
      distanceMi: 4.15, located: 'MP 108.5 measured back 0.8 mi from the terminus along the OSM way',
      source: 'FERC EA 2016-03-31 table A.4.a-1',
      changeNote: 'Same 20-inch pipe as the terminus.',
    }),
    pt('transco-whitfield-entry', -84.892639, 34.707247, {
      line: 'Transco', lineName: TRANSCO, label: '20″', kind: 'diameter',
      value: 'Whitfield County entry at EA MP 107.3 (from Murray County), 20-inch.',
      distanceMi: 5.17, located: 'MP 107.3 measured back 2.0 mi from the terminus along the OSM way',
      source: 'FERC EA 2016-03-31 table A.4.a-1',
      changeNote: 'Same 20-inch pipe as the terminus.',
    }),
    pt('transco-agl-spur', -84.862188, 34.698271, {
      line: 'Transco', lineName: TRANSCO, label: '16″ spur', kind: 'valve',
      value: 'MLV-11 and the AGL Spur tap at MP 105.2 (Murray County): a 2.0-mi 16-inch spur to the Murray Meter Station (50,000 Dth/d, Atlanta Gas Light).',
      distanceMi: 6.94, located: 'MP 105.2 measured back 4.1 mi from the terminus along the OSM way',
      source: 'FERC EA table A.4.b-1; order 2016-08-03',
      changeNote: 'Branch to AGL, not a change in the lateral.',
    }),
    pt('transco-mlv10', -84.852175, 34.631324, {
      line: 'Transco', lineName: TRANSCO, label: 'MLV', kind: 'valve',
      value: 'Mainline valve MLV-10 at MP 98.7 (Murray County), 20-inch pipe.',
      distanceMi: 9.03, located: 'MP 98.7 measured back 10.6 mi from the terminus along the OSM way',
      source: 'FERC EA table A.4.b-1',
      changeNote: '',
    }),
  ],
};
